/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 *  ╔═○─┐ emotive
 *    ●●  ENGINE
 *  └─○═╝
 *                   ◐ ◑ ◒ ◓  TTS MANAGER  ◓ ◒ ◑ ◐
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 *
 * @fileoverview TTSManager - Text-to-Speech Synthesis and Control
 * @author Emotive Engine Team
 * @version 1.0.0
 * @module TTSManager
 *
 * ╔═══════════════════════════════════════════════════════════════════════════════════
 * ║                                   PURPOSE
 * ╠═══════════════════════════════════════════════════════════════════════════════════
 * ║ Manages text-to-speech synthesis using the Web Speech API. Provides voice
 * ║ selection, speech state management, and visual feedback coordination during
 * ║ TTS playback.
 * ╚═══════════════════════════════════════════════════════════════════════════════════
 *
 * ┌───────────────────────────────────────────────────────────────────────────────────
 * │ 🗣️ RESPONSIBILITIES
 * ├───────────────────────────────────────────────────────────────────────────────────
 * │ • Query available TTS voices from browser
 * │ • Speak text using Web Speech API (delegates to SpeechManager)
 * │ • Track TTS speaking state
 * │ • Coordinate visual feedback during speech
 * │ • Stop ongoing TTS playback
 * └───────────────────────────────────────────────────────────────────────────────────
 *
 * ════════════════════════════════════════════════════════════════════════════════════
 */

export class TTSManager {
    /**
     * Create TTSManager
     *
     * @param {Object} deps - Dependencies
     * @param {Object} [deps.speechManager] - Speech manager instance
     * @param {Object} [deps.renderer] - Renderer instance
     * @param {Object} deps.tts - TTS state object with available, speaking properties
     * @param {Object} deps.state - Shared state with ttsSpeaking, speaking properties
     * @param {Object} [deps.chainTarget] - Return value for method chaining
     */
    constructor(deps) {
        // Required dependency validation
        if (!deps.tts) throw new Error('TTSManager: tts required');
        if (!deps.state) throw new Error('TTSManager: state required');

        this.speechManager = deps.speechManager || null;
        this.renderer = deps.renderer || null;
        this.tts = deps.tts;
        this._state = deps.state;
        this._chainTarget = deps.chainTarget || this;
    }

    /**
     * Gets available TTS voices from the browser
     * @returns {Array} Array of available voice objects
     *
     * @example
     * const voices = mascot.getTTSVoices();
     * voices.forEach(voice => {
     *   console.log(`${voice.name} (${voice.lang})`);
     * });
     */
    getTTSVoices() {
        if (!this.tts.available) {
            return [];
        }

        return window.speechSynthesis.getVoices();
    }

    /**
     * Checks if TTS is currently speaking
     * @returns {boolean} True if currently speaking
     *
     * @example
     * if (mascot.isTTSSpeaking()) {
     *   console.log('Mascot is speaking');
     * }
     */
    isTTSSpeaking() {
        return this.tts.speaking;
    }

    /**
     * Speak text using TTS (delegates to SpeechManager for actual synthesis)
     * @param {string} text - Text to speak
     * @param {Object} options - Speech options (voice, rate, pitch, etc.)
     * @returns {EmotiveMascot} Parent mascot instance for chaining
     *
     * @example
     * mascot.speak('Hello world', {
     *   voice: voices[0],
     *   rate: 1.0,
     *   pitch: 1.0
     * });
     */
    speak(text, options = {}) {
        return this.speechManager ? this.speechManager.speak(text, options) : this._chainTarget;
    }

    /**
     * Set TTS speaking state and trigger visual animation
     * @param {boolean} speaking - Whether TTS is speaking
     *
     * @example
     * mascot.setTTSSpeaking(true); // Start visual feedback
     * // ... TTS plays ...
     * mascot.setTTSSpeaking(false); // Stop visual feedback
     */
    setTTSSpeaking(speaking) {
        this._state.ttsSpeaking = speaking;

        // Update renderer if using Emotive style
        if (this.renderer && this.renderer.startSpeaking) {
            if (speaking) {
                this.renderer.startSpeaking();
            } else {
                this.renderer.stopSpeaking();
            }
        }

        // Also update the speaking flag for compatibility
        this._state.speaking = speaking;
    }

    /**
     * Get available TTS voices (alias for getTTSVoices)
     * @returns {Array} Array of available voices
     *
     * @example
     * const voices = mascot.getVoices();
     * console.log(`${voices.length} voices available`);
     */
    getVoices() {
        if (!window.speechSynthesis) {
            return [];
        }
        return window.speechSynthesis.getVoices();
    }

    /**
     * Stop any ongoing TTS speech immediately
     *
     * @example
     * mascot.speak('This is a long sentence that will be interrupted');
     * setTimeout(() => mascot.stopTTS(), 500); // Stop after 500ms
     */
    stopTTS() {
        if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
            this.setTTSSpeaking(false);
        }
    }
}
